// src/components/pdf-viewer/PdfSkeleton.js
import React, { useRef, useState, useLayoutEffect } from 'react';
import Skeleton from '../ui/Skeleton';

// Letter-size aspect ratio (height / width)
const LETTER_ASPECT_RATIO = 11 / 8.5;
const MIN_PDF_HEIGHT = 200 * LETTER_ASPECT_RATIO;
const PADDING = 40;

// BASE SIZE: All content is designed at this size, then scaled
const BASE_HEIGHT = 792; // Standard letter height in points at 72 DPI (11 inches)
const BASE_WIDTH = BASE_HEIGHT / LETTER_ASPECT_RATIO;

/**
 * Skeleton loader for PDF with stamp
 * Shows a letter-sized PDF skeleton with a stamp skeleton in the bottom right
 * Uses CSS transform scale for proportional scaling of all elements
 */
const PdfSkeleton = ({ documentName = 'Document', displayHeight: propDisplayHeight = 600 }) => {
  const skeletonContainerRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const [measuredHeight, setMeasuredHeight] = useState(propDisplayHeight);

  // Use the prop if provided and not default, otherwise use measured height
  const displayHeight = propDisplayHeight !== 600 ? propDisplayHeight : measuredHeight;

  // Calculate height from container dimensions
  const calculateHeight = (containerWidth, containerHeight) => {
    if (!containerWidth || !containerHeight) {
      return MIN_PDF_HEIGHT;
    }

    const availableWidth = containerWidth - PADDING;
    const availableHeight = containerHeight - PADDING;
    const heightFromWidth = availableWidth * LETTER_ASPECT_RATIO;
    const optimalHeight = Math.min(heightFromWidth, availableHeight);
    const finalHeight = Math.max(optimalHeight, MIN_PDF_HEIGHT);
    
    return finalHeight;
  };

  // Measure the container on mount + set up ResizeObserver
  useLayoutEffect(() => {
    const container = skeletonContainerRef.current?.parentElement;
    if (!container) {
      console.log('⚠️ Skeleton: Container not found');
      return;
    }

    console.log('✅ Skeleton: Container found, setting up measurement and observer');

    // Initial measurement
    const rect = container.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      const initialHeight = calculateHeight(rect.width, rect.height);
      setMeasuredHeight(initialHeight);
      console.log('🎨 Skeleton initial measurement:', {
        containerSize: { width: rect.width, height: rect.height },
        calculatedHeight: initialHeight.toFixed(1)
      });
    }

    // Create ResizeObserver for dynamic updates
    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        const newHeight = calculateHeight(width, height);
        
        setMeasuredHeight((prevHeight) => {
          if (Math.abs(newHeight - prevHeight) > 5) {
            console.log('🔄 Skeleton ResizeObserver updating height:', {
              from: prevHeight.toFixed(1),
              to: newHeight.toFixed(1),
              containerSize: { width, height }
            });
            return newHeight;
          }
          return prevHeight;
        });
      }
    });

    resizeObserverRef.current.observe(container);
    console.log('👀 Skeleton: ResizeObserver connected');

    // Cleanup
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        console.log('👋 Skeleton: ResizeObserver disconnected');
      }
    };
  }, [skeletonContainerRef.current]);

  // Calculate the scale factor based on desired vs base height
  const scale = displayHeight / BASE_HEIGHT;
  
  // Stamp dimensions at BASE size (approximately 2.5" x 0.75" on letter paper)
  const BASE_STAMP_WIDTH = (1.33 / 8.5) * BASE_WIDTH;
  const BASE_STAMP_HEIGHT = (0.75 / 11) * BASE_HEIGHT;
  const BASE_STAMP_PADDING = 20;

  return (
    <div ref={skeletonContainerRef} className="h-full flex items-start pt-[20px] justify-center">
      {/* Outer container that holds the scaled content */}
      <div 
        style={{
          width: `${BASE_WIDTH * scale}px`,
          height: `${BASE_HEIGHT * scale}px`,
          overflow: 'hidden'
        }}
      >
        {/* Inner content at BASE size, scaled via transform */}
        <div
          style={{
            width: `${BASE_WIDTH}px`,
            height: `${BASE_HEIGHT}px`,
            transform: `scale(${scale})`,
            transformOrigin: '0 0'
          }}
        >
          <div className="relative">
            {/* Main PDF skeleton */}
            <Skeleton
              className="bg-app-gray-300"
              style={{
                width: `${BASE_WIDTH}px`,
                height: `${BASE_HEIGHT}px`,
                borderRadius: '2px'
              }}
            >
              {/* Lines to simulate text content - all at BASE size */}
              <div className="p-14 space-y-3">
                <Skeleton 
                  className="bg-app-gray-400 h-4 w-3/4"
                  style={{ borderRadius: '2px' }}
                />
                <Skeleton 
                  className="bg-app-gray-400 h-4 w-full"
                  style={{ borderRadius: '2px' }}
                />
                <Skeleton 
                  className="bg-app-gray-400 h-4 w-5/6"
                  style={{ borderRadius: '2px' }}
                />
                <div className="pt-2" />
                <Skeleton 
                  className="bg-app-gray-400 h-4 w-full"
                  style={{ borderRadius: '2px' }}
                />
                <Skeleton 
                  className="bg-app-gray-400 h-4 w-4/5"
                  style={{ borderRadius: '2px' }}
                />
                <Skeleton 
                  className="bg-app-gray-400 h-4 w-full"
                  style={{ borderRadius: '2px' }}
                />
              </div>
              
              {/* Stamp skeleton in bottom right - at BASE size */}
              <Skeleton
                className="absolute bg-app-gray-400 border-8 border-app-gray-500"
                style={{
                  width: `${BASE_STAMP_WIDTH}px`,
                  height: `${BASE_STAMP_HEIGHT}px`,
                  bottom: `${BASE_STAMP_PADDING}px`,
                  right: `${BASE_STAMP_PADDING}px`,
                  borderRadius: '8px'
                }}
              />
            </Skeleton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdfSkeleton;